import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';

import { RouterLink } from 'src/components/router-link';
import { Scrollbar } from 'src/components/scrollbar';
import { usePathname } from 'src/hooks/use-pathname';
import { visitorInfo } from 'src/hooks/use-visitorInfo';
import { paths } from 'src/paths';
import { DataHelper } from 'src/services/dataHelper';
import { WhatsApp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getNavCssVars, SIDE_NAV_WIDTH } from '../nav-styles';
import { TenantSwitch } from '../tenant-switch';
import { SideNavSection } from './side-nav-section';

const WhatsAppButton = () => {
  const { t } = useTranslation();

  return (
    <Button
      component="a"
      fullWidth
      href="https://wa.me/971563813744?text=Hello%2C%20I%20would%20like%20to%20know%20more%20about%20your%20services!"
      onClick={async () => {
        await new DataHelper().save('ContractUs', {
          ID: 0,
          mobile: null,
          iP: visitorInfo().ip,
          country: visitorInfo().country,
          city: visitorInfo().city,
          region: visitorInfo().region,
          latitude: visitorInfo().latitude,
          longitude: visitorInfo().longitude,
          dateTime: new Date().toISOString()
        });
      }}
      rel="noopener noreferrer"
      size="small"
      startIcon={(
        <SvgIcon>
          <WhatsApp />
        </SvgIcon>
      )}
      sx={{
        bgcolor: '#25D366',
        borderRadius: 2,
        fontSize: '0.8125rem',
        fontWeight: 600,
        py: 0.85,
        textTransform: 'none',
        '&:hover': { bgcolor: '#1ebe57' }
      }}
      target="_blank"
      variant="contained"
    >
      +971 56 381 3744
    </Button>
  );
};

export const SideNav = (props) => {
  const { color = 'evident', sections = [] } = props;
  const pathname = usePathname();
  const theme = useTheme();
  const { t } = useTranslation();
  const cssVars = useMemo(() => getNavCssVars(theme, color), [theme, color]);

  return (
    <Drawer
      anchor="left"
      open
      PaperProps={{
        sx: {
          ...cssVars,
          backgroundColor: 'var(--nav-bg)',
          borderRight: '1px solid var(--nav-border-color)',
          color: 'var(--nav-color)',
          width: SIDE_NAV_WIDTH
        }
      }}
      variant="permanent"
    >
      <Scrollbar
        sx={{
          height: '100%',
          '& .simplebar-content': { height: '100%' },
          '& .simplebar-scrollbar:before': { background: 'var(--nav-scrollbar-color)' }
        }}
      >
        <Stack sx={{ height: '100%' }}>
          <Stack alignItems="center" direction="row" spacing={1.5} sx={{ px: 2, py: 2 }}>
            <Box
              component={RouterLink}
              href={paths.index}
              sx={{
                alignItems: 'center',
                bgcolor: 'primary.main',
                border: '1px solid var(--nav-logo-border)',
                borderRadius: 2,
                color: 'primary.contrastText',
                display: 'flex',
                flexShrink: 0,
                fontSize: '0.75rem',
                fontWeight: 800,
                height: 38,
                justifyContent: 'center',
                letterSpacing: '-0.02em',
                textDecoration: 'none',
                width: 38
              }}
            >
              AS
            </Box>
            <TenantSwitch sx={{ flexGrow: 1, minWidth: 0 }} />
          </Stack>

          <Divider sx={{ borderColor: 'var(--nav-border-color)', mx: 2 }} />

          <Stack component="nav" spacing={1} sx={{ flexGrow: 1, px: 1.25, py: 1.5 }}>
            {sections.map((section, index) => (
              <SideNavSection
                items={section.items}
                key={index}
                pathname={pathname}
                subheader={section.subheader}
              />
            ))}
          </Stack>

          <Box sx={{ p: 2, pt: 0 }}>
            <Box
              sx={{
                bgcolor: 'var(--nav-footer-bg)',
                border: '1px solid var(--nav-footer-border)',
                borderRadius: 2.5,
                p: 1.5
              }}
            >
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, mb: 0.25 }}>
                {t('For Sales?', 'للمبيعات؟')}
              </Typography>
              <Typography sx={{ color: 'var(--nav-item-color)', fontSize: '0.75rem', mb: 1.25 }} variant="body2">
                {t('Please contact us', 'تواصل معنا')}.
              </Typography>
              <WhatsAppButton />
            </Box>
          </Box>
        </Stack>
      </Scrollbar>
    </Drawer>
  );
};

SideNav.propTypes = {
  color: PropTypes.oneOf(['blend-in', 'discreet', 'evident']),
  sections: PropTypes.array
};
